import React, { useState } from 'react';
import { useSearchMatchesFinished } from './matchesHooks';
import { useTournamentStore } from '../../service/useTournamentStore';
import type { MatchDetail } from '@iziping/common';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import Modal from '../modal/Modal';
import ModifyMatch from './ModifyMatch';
import { displayRoundDetail } from '@iziping/common';
import { Search, Trophy } from 'lucide-react';
import { EmptyState, InputIcon } from '@iziping/ui';

const FinishedMatchesManagement: React.FC = () => {
    const { selectedTournament } = useTournamentStore();
    const { t } = useTranslation();
    const {
        data: matchesFinished,
        isLoading,
        isError,
    } = useSearchMatchesFinished({ tournamentId: selectedTournament!.id });

    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const toggleModal = () => setShowModal(!showModal);
    const closeModal = () => setShowModal(false);

    const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);

    const handleRowClick = (match: MatchDetail) => {
        setModalContent(
            <ModifyMatch
                id={match.id}
                onSuccess={() => {
                    closeModal();
                }}
            />
        );
        setShowModal(true);
    };

    return (
        <div className={'page'}>
            {matchesFinished && matchesFinished.length > 0 ? (
                <>
                    <div>
                        <h2>Matchs terminés</h2>
                        <InputIcon
                            icon={Search}
                            placeholder={t('common.search') as string}
                            value={searchTerm}
                            small={true}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <table className={'table-stripped'}>
                        <thead>
                            <tr>
                                <th>Série</th>
                                <th>Num table</th>
                                <th>Type</th>
                                <th>Début</th>
                                <th>Fin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matchesFinished
                                .filter((match: MatchDetail) => {
                                    const term = searchTerm.toLowerCase();
                                    if (!term) return true;
                                    if (match.heatName.toLowerCase().includes(term)) return true;
                                    return match.opponents.some((o) =>
                                        o.heatRegistration.players.some(
                                            (p) =>
                                                p.firstname.toLowerCase().includes(term) ||
                                                p.lastname.toLowerCase().includes(term)
                                        )
                                    );
                                })
                                .map((match: MatchDetail) => (
                                    <tr
                                        key={match.id}
                                        onClick={() => handleRowClick(match)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{match.heatName}</td>
                                        <td>{match.table?.number}</td>
                                        <td>{displayRoundDetail(match)}</td>
                                        <td>{match.begin ? format(match.begin, 'HH:mm:ss') : '-'}</td>
                                        <td>{match.end ? format(match.end, 'HH:mm:ss') : '-'}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </>
            ) : (
                !isLoading && <EmptyState icon={<Trophy size={56} />} message={t('matches.finished.empty')} />
            )}

            {showModal && modalContent && <Modal onClose={closeModal}>{modalContent}</Modal>}
        </div>
    );
};

export default FinishedMatchesManagement;
